import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, Loader2, ExternalLink, FileText, BookOpen } from "lucide-react";
import { enrollmentApi } from "../../../lib";

interface EnrollmentCourse {
  _id: string;
  title: string;
  thumbnail?: string;
}

interface ExamStatus {
  hasCertificateExam: boolean;
  examPassed: boolean;
  examScore?: number;
  examAttempts: number;
  examAttemptsAllowed?: number;
  certificateIssued?: boolean;
  lastAttempt?: { completedAt?: string };
}

interface EnrollmentItem {
  _id: string;
  course: EnrollmentCourse;
  status: string;
  progress: number;
  certificateIssued?: boolean;
  certificateUrl?: string;
  completedAt?: string;
}

type CertificateEntry = {
  _id: string;
  course: { _id: string; title: string; thumbnail?: string };
  issuedAt: string;
  certificateId: string;
};

type ExamEntry = {
  enrollmentId: string;
  courseId: string;
  courseTitle: string;
  courseThumbnail?: string;
  examPassed: boolean;
  certificateIssued: boolean;
  examAttempts: number;
  examAttemptsAllowed?: number;
};

const StudentCertificates = () => {
  const [certificates, setCertificates] = useState<CertificateEntry[]>([]);
  const [examEntries, setExamEntries] = useState<ExamEntry[]>([]);
  const [allCompletedEnrollments, setAllCompletedEnrollments] = useState<EnrollmentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await enrollmentApi.getUserEnrollments({ page: 1, limit: 50, status: "completed" });
        const items: EnrollmentItem[] = Array.isArray(res.data) ? res.data : [];
        setAllCompletedEnrollments(items);

        const earned: CertificateEntry[] = [];
        const exams: ExamEntry[] = [];

        await Promise.all(
          items.map(async (item) => {
            const courseId = item.course?._id;
            if (!courseId) return;
            try {
              const examRes = await enrollmentApi.getCertificateExamStatus(courseId);
              const examData: ExamStatus = examRes.data;
              if (examData.certificateIssued || item.certificateIssued) {
                earned.push({
                  _id: item._id,
                  course: {
                    _id: courseId,
                    title: item.course?.title || "Certificate",
                    thumbnail: item.course?.thumbnail,
                  },
                  issuedAt: item.completedAt || new Date().toISOString(),
                  certificateId: `enrollment-${item._id}`,
                });
              } else if (
                examData.hasCertificateExam &&
                !examData.examPassed &&
                (examData.examAttemptsAllowed === undefined || examData.examAttempts < examData.examAttemptsAllowed)
              ) {
                exams.push({
                  enrollmentId: item._id,
                  courseId,
                  courseTitle: item.course?.title || "Certificate Exam",
                  courseThumbnail: item.course?.thumbnail,
                  examPassed: !!examData.examPassed,
                  certificateIssued: !!examData.certificateIssued,
                  examAttempts: examData.examAttempts || 0,
                  examAttemptsAllowed: examData.examAttemptsAllowed,
                });
              }
            } catch {
              // skip courses we cannot evaluate
            }
          }),
        );

        setCertificates(earned);
        setExamEntries(exams);
      } catch (err: any) {
        console.error("Certificates load error:", err);
        setError(err?.message || "Failed to load certificates");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const hasContent = certificates.length > 0 || examEntries.length > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 mx-auto mb-3 text-orange-500 animate-spin" />
          <p className="text-xs text-gray-500">Loading certificates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 border border-red-200 rounded-lg bg-red-50">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="py-4 md:py-6">
      <div className="px-4 mx-auto max-w-7xl md:px-6 lg:px-8">
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-1">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg shadow-sm bg-gradient-to-r from-amber-600 to-orange-500">
              <Award className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-lg font-bold text-gray-900 md:text-xl">
              My Certificates
            </h1>
          </div>
          <p className="text-[10px] text-gray-500 ml-10 md:text-xs">
            View and download your earned certificates
          </p>
        </div>

        {!hasContent ? (
          <div className="py-12 text-center bg-white border border-gray-100 shadow-sm rounded-xl">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-br from-gray-100 to-gray-200">
              <Award className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="mb-1 text-sm font-semibold text-gray-900">
              No certifications yet
            </h3>
            <p className="text-[10px] text-gray-500 mb-4 max-w-xs mx-auto">
              Complete courses and pass the certificate exam to earn certificates
            </p>
            <Link
              to="/courses"
              className="inline-flex items-center gap-2 px-4 py-2 text-[11px] font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all"
            >
              Browse Courses
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {certificates.length > 0 && (
              <div className="space-y-3">
                {certificates.map((cert) => (
                  <div
                    key={cert._id}
                    className="flex items-center gap-4 p-4 transition-all duration-200 bg-white border border-gray-100 rounded-xl hover:shadow-md"
                  >
                    {cert.course?.thumbnail && (
                      <div className="flex-shrink-0 w-20 h-20 overflow-hidden bg-gray-100 rounded-lg">
                        <img
                          src={cert.course.thumbnail}
                          alt={cert.course.title}
                          className="object-cover w-full h-full"
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="mb-1 text-sm font-bold text-gray-900 line-clamp-1">
                        {cert.course?.title || "Certificate"}
                      </h3>
                      <div className="flex items-center gap-3 text-[10px] text-gray-500">
                        <span>
                          Issued on{" "}
                          {new Date(cert.issuedAt).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </span>
                        <span className="text-gray-300">•</span>
                        <span className="font-mono">
                          ID: {cert.certificateId || "N/A"}
                        </span>
                      </div>
                    </div>
                    <Link
                      to={`/certificate?courseId=${cert.course._id}`}
                      className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-[10px] font-medium text-white rounded-lg bg-gradient-to-r from-amber-600 to-orange-500 hover:shadow-md transition-all"
                    >
                      <ExternalLink className="w-3 h-3" />
                      View
                    </Link>
                  </div>
                ))}
              </div>
            )}

            {examEntries.length > 0 && (
              <div className="space-y-3">
                <h3 className="mt-6 mb-1 text-sm font-semibold text-gray-900">
                  Certificate Exams
                </h3>
                {examEntries.map((entry) => (
                  <div
                    key={entry.enrollmentId}
                    className="flex items-center gap-4 p-4 transition-all duration-200 bg-white border border-gray-100 rounded-xl hover:shadow-md"
                  >
                    {entry.courseThumbnail && (
                      <div className="flex-shrink-0 w-20 h-20 overflow-hidden bg-gray-100 rounded-lg">
                        <img
                          src={entry.courseThumbnail}
                          alt={entry.courseTitle}
                          className="object-cover w-full h-full"
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="mb-1 text-sm font-bold text-gray-900 line-clamp-1">
                        {entry.courseTitle}
                      </h3>
                      <p className="text-[10px] text-gray-500">
                        {entry.certificateIssued
                          ? "Certificate ready"
                          : "Pass the exam to earn your certificate"}
                      </p>
                    </div>
                    <Link
                      to={`/course/${entry.courseId}/certificate-exam`}
                      className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-[10px] font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all"
                    >
                      <FileText className="w-3 h-3" />
                      {entry.examPassed ? "View Exam" : "Take Certificate Exam"}
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentCertificates;